import React, {useState, useEffect} from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {APIClient} from '../../utils/APIClient';
import {API_URL} from '../../settings/local_settings';
import {AccessService} from '../../utils/AccessService';
import {LayoutPortal} from '../../LayoutPortal';
import {usePortalClientId} from '../../hooks/usePortalClientId';
import {usePortalModules} from '../../hooks/usePortalModules';
import {usePreviewContext} from '../../context/PreviewContext';
import {Spinner} from '../Spinner';
import {FaIcon} from '../FaIcon';
import {ChannelLogo} from '../channels/ChannelLogo';
import {CHANNEL_INFO_BY_CHANNEL} from '../channels/channels';
import {AdAccountsActionDataHandler} from '../AdAccountsActionDataHandler';
import {
    FULL_PERIOD_BUDGET_PROPORTIONAL_COLUMN,
    GOAL_1_COLUMN, GOAL_2_COLUMN,
    CURRENT_PERIOD_ALLOCATED_BUDGET_COLUMN,
    CURRENT_PERIOD_COST_COLUMN,
} from '../dashboard/columns';
import {
    LEVEL_COLORS, getTodayAlert, IndicatorBadge, BudgetProgressBar,
    SemaphoreIndicator, CHANNEL_BOOLEAN_MAP, formatCurrency,
} from './portalUtils';

// Selection method display map (exported for reuse in PortalHome)
export const SELECTION_METHOD_I18N = {
    'INCLUDE_ALL_CAMPAIGNS': 'portal.budgets.method_all_campaigns',
    'SELECT_CAMPAIGNS_FROM_LIST': 'portal.budgets.method_select_campaigns',
    'INCLUDE_CAMPAIGN_ON_CONDITION': 'portal.budgets.method_condition_campaigns',
    'SELECT_ADGROUPS_FROM_LIST': 'portal.budgets.method_select_adgroups',
    'INCLUDE_ADGROUP_ON_CONDITION': 'portal.budgets.method_condition_adgroups',
    'ECOMMERCE_INCLUDE_ALL_CHANNELS_AND_TYPES': 'portal.budgets.method_ecommerce_all',
    'ECOMMERCE_SELECT_CHANNELS_AND_TYPES': 'portal.budgets.method_ecommerce_select',
    'INCLUDE_ALL_ANALYTICS_SOURCE_MEDIUM': 'portal.budgets.method_analytics_all',
    'INCLUDE_ANALYTICS_SOURCE_MEDIUM_ON_CONDITION': 'portal.budgets.method_analytics_condition',
    'ANALYTICS_SELECT_SOURCES_MEDIUMS': 'portal.budgets.method_analytics_select',
};

export const hasCondition = (method) =>
    method === 'INCLUDE_CAMPAIGN_ON_CONDITION' ||
    method === 'INCLUDE_ADGROUP_ON_CONDITION' ||
    method === 'INCLUDE_ANALYTICS_SOURCE_MEDIUM_ON_CONDITION';

// --- Ad Account Group (inside detail) ---

export const AdAccountGroup = ({adAccountData, campaignsForAccount, buccConfig, t}) => {
    const channel = adAccountData.channel;
    const info = CHANNEL_INFO_BY_CHANNEL[channel];
    const accountName = adAccountData.ad_account_name || adAccountData.ad_account_account_id;

    // Conversion events
    const selectedItems = adAccountData.items || [];
    const emptyItem = adAccountData.empty_items_item;

    // Selection method from BUCC config
    const selectionMethod = buccConfig?.selection_method;
    const methodLabel = selectionMethod ? SELECTION_METHOD_I18N[selectionMethod] : null;
    const showCondition = selectionMethod && hasCondition(selectionMethod);

    return (
        <div className="portal-bu-ad-account-group">
            <div className="portal-bu-ad-account-header">
                {info && (
                    <span className="portal-bu-ad-account-channel">
                        <ChannelLogo channel={channel} height={16} width={16} hspace={0} vspace={0}/>
                    </span>
                )}
                <span className="portal-bu-ad-account-name">
                    {info?.Spanish || channel} — {accountName}
                </span>
            </div>

            {methodLabel && (
                <div className="portal-bu-selection-method">
                    <span className="portal-bu-selection-method-label">
                        {t('portal.budgets.selection_method')}:
                    </span>
                    <span className="portal-bu-selection-method-tag">
                        {t(methodLabel)}
                    </span>
                </div>
            )}

            {showCondition && (buccConfig.include_containing || buccConfig.exclude_containing) && (
                <div className="portal-bu-selection-condition">
                    {buccConfig.include_containing && (
                        <span className="portal-bu-selection-condition-item">
                            {t('portal.budgets.condition_include')} <code>{buccConfig.include_containing}</code>
                        </span>
                    )}
                    {buccConfig.exclude_containing && (
                        <span className="portal-bu-selection-condition-item">
                            {t('portal.budgets.condition_exclude')} <code>{buccConfig.exclude_containing}</code>
                        </span>
                    )}
                </div>
            )}

            <div className="portal-bu-conversion-tags">
                <span className="portal-bu-conversion-tag" style={{fontWeight: 600}}>
                    {t('portal.budgets.conversion_events')}:
                </span>
                {selectedItems.length > 0 ? (
                    selectedItems.map((name, idx) => (
                        <span key={idx} className="portal-bu-conversion-tag">{name}</span>
                    ))
                ) : emptyItem?.is_ok ? (
                    <span className="portal-bu-conversion-tag -all">{t('portal.budgets.all_primary')}</span>
                ) : (
                    <span className="portal-bu-conversion-tag -empty">{t('portal.budgets.no_events')}</span>
                )}
            </div>

            {campaignsForAccount ? (
                campaignsForAccount.length > 0 ? (
                    <div className="portal-bu-campaign-list">
                        {campaignsForAccount.map((camp, idx) => (
                            <div key={idx} className="portal-bu-campaign-row">
                                <span className="portal-bu-campaign-name">{camp.name}</span>
                                <span className={`portal-bu-campaign-status ${camp.active ? '-active' : '-paused'}`}>
                                    {camp.active ? t('portal.budgets.campaign_active') : t('portal.budgets.campaign_paused')}
                                </span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="portal-bu-campaign-all">{t('portal.budgets.all_campaigns')}</div>
                )
            ) : null}
        </div>
    );
};

// --- BU Campaigns Detail (collapsible) ---

export const BUCampaignsDetail = ({buId, t}) => {
    const [loading, setLoading] = useState(true);
    const [adAccountsData, setAdAccountsData] = useState(null);
    const [campaignsConfig, setCampaignsConfig] = useState(null);

    useEffect(() => {
        // Fetch both in parallel
        const fetchConfigs = new APIClient(API_URL + 'business-unit/' + buId + '/campaigns-configurations').call('GET');
        const fetchAdAccounts = new APIClient(API_URL + 'business-unit/' + buId + '/ad-accounts-data/').call('GET');

        Promise.all([fetchConfigs, fetchAdAccounts]).then(([configData, adData]) => {
            if (configData?.status === 'OK') {
                setCampaignsConfig(configData.data);
            }
            if (adData?.status === 'OK') {
                const handler = new AdAccountsActionDataHandler(adData.data, t);
                setAdAccountsData(handler.getAdAccountsSelectedItemsData());
            }
            setLoading(false);
        }).catch(() => setLoading(false));
    }, [buId, t]);

    if (loading) {
        return (
            <div className="portal-bu-panel-detail">
                <div className="portal-bu-panel-detail-loading">
                    <Spinner color="positive" size="sm"/>
                    <span>{t('portal.budgets.loading_campaigns')}</span>
                </div>
            </div>
        );
    }

    if (!adAccountsData || adAccountsData.length === 0) {
        return (
            <div className="portal-bu-panel-detail">
                <div className="portal-bu-panel-detail-empty">{t('portal.budgets.no_campaigns')}</div>
            </div>
        );
    }

    // Build maps from campaignsConfig (dict keyed by BUCC ID)
    const campaignsByBucc = {};
    const configByBucc = {};
    if (campaignsConfig) {
        const configList = Object.values(campaignsConfig);
        configList.forEach(bucc => {
            const buccId = bucc.id || bucc.ad_account_id;
            campaignsByBucc[buccId] = bucc.campaigns || [];
            configByBucc[buccId] = bucc;
        });
    }

    return (
        <div className="portal-bu-panel-detail">
            {adAccountsData.map((acc, idx) => (
                <AdAccountGroup
                    key={idx}
                    adAccountData={acc}
                    campaignsForAccount={campaignsByBucc[acc.bucc_id] || null}
                    buccConfig={configByBucc[acc.bucc_id] || null}
                    t={t}
                />
            ))}
        </div>
    );
};

// --- BU Panel ---

const BUPanel = ({bu, modules, basePath, t}) => {
    const [expanded, setExpanded] = useState(false);
    const [metrics, setMetrics] = useState(null);
    const [loadingMetrics, setLoadingMetrics] = useState(true);

    useEffect(() => {
        new APIClient(API_URL + 'business-unit/' + bu.id + '/dashboard/metrics').call('GET').then((data) => {
            if (data?.status === 'OK') {
                setMetrics(data.data);
            }
            setLoadingMetrics(false);
        }).catch(() => setLoadingMetrics(false));
    }, [bu.id]);

    // Build channels from boolean flags
    let channels = bu.channels;
    if (!Array.isArray(channels)) {
        channels = CHANNEL_BOOLEAN_MAP
            .filter(({field}) => bu[field])
            .map(({channel}) => channel);
    }

    // Extract indicator data
    const budgetCol = metrics?.[FULL_PERIOD_BUDGET_PROPORTIONAL_COLUMN];
    const budgetAlert = getTodayAlert(budgetCol);
    const budgetLevel = budgetAlert?.level || 'normal';

    const allocatedBudgetCol = metrics?.[CURRENT_PERIOD_ALLOCATED_BUDGET_COLUMN];
    const costCol = metrics?.[CURRENT_PERIOD_COST_COLUMN];

    const goal1Col = metrics?.[GOAL_1_COLUMN];
    const goal1Alert = getTodayAlert(goal1Col);
    const goal1Level = goal1Alert?.level || 'normal';
    const hasGoal1 = goal1Col?.has_goal;

    const goal2Col = metrics?.[GOAL_2_COLUMN];
    const goal2Alert = getTodayAlert(goal2Col);
    const goal2Level = goal2Alert?.level || 'normal';
    const hasGoal2 = goal2Col?.has_goal;

    const costNum = costCol?.value ?? 0;
    const budgetNum = allocatedBudgetCol?.value ?? 0;

    const statusColor = LEVEL_COLORS[budgetLevel] || LEVEL_COLORS.normal;
    const hasModule = (mod) => modules && modules.includes(mod);

    return (
        <div className="portal-bu-panel" style={{'--bu-status-color': statusColor}}>
            <div
                className="portal-bu-panel-summary"
                onClick={() => setExpanded(!expanded)}
            >
                {/* Row 1: semaphore + name + channels */}
                <div className="portal-bu-panel-summary-top">
                    <SemaphoreIndicator level={budgetLevel}/>
                    <h3 className="portal-bu-panel-name">{bu.name}</h3>
                    <div className="portal-bu-panel-channels">
                        {channels.map(ch => {
                            const info = CHANNEL_INFO_BY_CHANNEL[ch];
                            return info ? (
                                <span key={ch} className="portal-bu-panel-channel" title={info.Spanish}>
                                    <ChannelLogo channel={ch} height={16} width={16} hspace={0} vspace={0}/>
                                </span>
                            ) : null;
                        })}
                    </div>
                </div>

                {/* Row 2: Metrics (budget, cost+badge, progress bar) */}
                {!loadingMetrics && metrics && (
                    <div className="portal-bu-panel-metrics">
                        <SemaphoreIndicator level={budgetLevel}/>
                        <div className="portal-bu-panel-metric">
                            <span className="portal-bu-panel-metric-label">{t('portal.budgets.budget')}</span>
                            <span className="portal-bu-panel-metric-value">
                                {allocatedBudgetCol?.formatted_value || (budgetNum ? formatCurrency(budgetNum) : '—')}
                            </span>
                        </div>
                        <div className="portal-bu-panel-metric">
                            <span className="portal-bu-panel-metric-label">{t('portal.budgets.spent')}</span>
                            <div className="portal-bu-panel-metric-row">
                                <span className="portal-bu-panel-metric-value">
                                    {costCol?.formatted_value || (costNum ? formatCurrency(costNum) : '—')}
                                </span>
                                <IndicatorBadge alert={budgetAlert}/>
                            </div>
                        </div>
                        <div className="portal-bu-panel-progress-wrap">
                            <BudgetProgressBar cost={costNum} budget={budgetNum}/>
                        </div>
                    </div>
                )}
                {loadingMetrics && (
                    <div className="portal-bu-panel-metrics">
                        <Spinner color="positive" size="sm"/>
                    </div>
                )}

                {/* Row 3: Goals with individual semaphores */}
                {!loadingMetrics && (hasGoal1 || hasGoal2) && (
                    <div className="portal-bu-panel-goals">
                        {hasGoal1 && (
                            <div className="portal-bu-panel-goal">
                                <SemaphoreIndicator level={goal1Level}/>
                                <span className="portal-bu-panel-goal-label">
                                    {t('portal.budgets.goal_label')}: {goal1Col.metric_name || 'Goal 1'}
                                </span>
                                {goal1Col.formatted_value && (
                                    <span className="portal-bu-panel-goal-value">
                                        {goal1Col.formatted_value}
                                    </span>
                                )}
                                <IndicatorBadge alert={goal1Alert}/>
                            </div>
                        )}
                        {hasGoal2 && (
                            <div className="portal-bu-panel-goal">
                                <SemaphoreIndicator level={goal2Level}/>
                                <span className="portal-bu-panel-goal-label">
                                    {t('portal.budgets.goal_label')}: {goal2Col.metric_name || 'Goal 2'}
                                </span>
                                {goal2Col.formatted_value && (
                                    <span className="portal-bu-panel-goal-value">
                                        {goal2Col.formatted_value}
                                    </span>
                                )}
                                <IndicatorBadge alert={goal2Alert}/>
                            </div>
                        )}
                    </div>
                )}

                {/* Row 4: Actions */}
                <div className="portal-bu-panel-actions">
                    {hasModule(AccessService.MODULE_AGENT) && (
                        <Link
                            className="portal-bu-panel-action-btn"
                            to={`${basePath}/agent?bu_id=${bu.id}`}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <FaIcon iconName="robot"/>
                            {t('portal.budgets.get_insights')}
                        </Link>
                    )}
                    {hasModule(AccessService.MODULE_DATA_EXPLORER) && (
                        <Link
                            className="portal-bu-panel-action-btn"
                            to={`${basePath}/data-explorer?bu_id=${bu.id}`}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <FaIcon iconName="chart-line"/>
                            {t('portal.budgets.explore_data')}
                        </Link>
                    )}
                    <button
                        className={`portal-bu-panel-expand-btn${expanded ? ' -expanded' : ''}`}
                        onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
                    >
                        {expanded ? t('portal.budgets.collapse') : t('portal.budgets.expand')}
                        <FaIcon iconName="chevron-down"/>
                    </button>
                </div>
            </div>

            {/* Collapsible detail */}
            {expanded && <BUCampaignsDetail buId={bu.id} t={t}/>}
        </div>
    );
};

// --- Main Component ---

export const PortalBudgets = () => {
    const {t} = useTranslation();
    const {isPreview, previewClientId} = usePreviewContext();
    const clientId = usePortalClientId();
    const modules = usePortalModules();
    const basePath = isPreview ? `/client/${previewClientId}/preview` : '/portal';

    const [businessUnits, setBusinessUnits] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!clientId) return;
        new APIClient(API_URL + 'client/' + clientId + '?portal=true').call('GET').then((data) => {
            if (data?.status === 'OK') {
                setBusinessUnits(data.data.business_units || []);
            }
            setLoading(false);
        });
    }, [clientId]);

    return (
        <LayoutPortal>
            <div className="portal-budgets">
                <div className="portal-page-header">
                    <h1 className="portal-page-title">{t('portal.budgets.title')}</h1>
                    <p className="portal-page-subtitle">{t('portal.budgets.subtitle')}</p>
                </div>

                {loading ? (
                    <div className="portal-loading">
                        <Spinner color="positive"/>
                    </div>
                ) : businessUnits && businessUnits.length > 0 ? (
                    <div className="portal-bu-panels">
                        {businessUnits.map(bu => (
                            <BUPanel key={bu.id} bu={bu} modules={modules} basePath={basePath} t={t}/>
                        ))}
                    </div>
                ) : (
                    <p className="portal-empty-state">{t('portal.budgets.no_business_units')}</p>
                )}
            </div>
        </LayoutPortal>
    );
};
